import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { MetricCard } from '../../components/ui/Card.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';
import { formatCurrency } from '../../utils/formatters.js';

import { PostCostModal } from './PostCostModal.jsx';
import { SetPlanModal } from './SetPlanModal.jsx';

const YEARS = [2025, 2026, 2027];

function Variance({ value, pct }) {
  const n = Number(value);
  const cls = n > 0 ? 'pos' : n < 0 ? 'neg' : '';
  const color = n > 0 ? 'var(--color-success, #059669)' : n < 0 ? 'var(--color-danger, #dc2626)' : 'inherit';
  return (
    <span className={`mono num ${cls}`} style={{ color }}>
      {formatCurrency(value)}{pct != null ? ` (${pct > 0 ? '+' : ''}${pct}%)` : ''}
    </span>
  );
}

export function ControllingPage() {
  const { t } = useTranslation();
  const [year, setYear] = useState(2026);
  const [planOpen, setPlanOpen] = useState(false);
  const [postOpen, setPostOpen] = useState(false);

  const report = useQuery({
    queryKey: ['finance', 'controlling', year],
    queryFn: () => financeApi.controllingReport(year).then((r) => r.items),
  });
  const rows = report.data?.rows || [];
  const totals = report.data?.totals;

  const columns = [
    { key: 'code', label: t('co.col.code'), render: (r) => <span className="mono">{r.code}{r.parentCode ? <span className="muted text-sm"> ◂ {r.parentCode}</span> : null}</span> },
    { key: 'name', label: t('co.col.name') },
    { key: 'planned', label: t('co.col.plan'), align: 'right', render: (r) => <span className="mono num">{formatCurrency(r.planned)}</span> },
    { key: 'actual', label: t('co.col.actual'), align: 'right', render: (r) => <span className="mono num">{formatCurrency(r.actual)}</span> },
    { key: 'variance', label: t('co.col.variance'), align: 'right', render: (r) => <Variance value={r.variance} pct={r.variancePct} /> },
  ];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('co.title')}</h1>
          <p className="muted">{t('co.subtitle')}</p>
        </div>
        <div className="page-actions">
          <select className="input" style={{ maxWidth: 110 }} value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <Button variant="secondary" onClick={() => setPlanOpen(true)}>{t('co.action.setPlan')}</Button>
          <Button variant="primary" onClick={() => setPostOpen(true)}>{t('co.action.postCost')}</Button>
        </div>
      </header>

      <div className="card-grid">
        <MetricCard label={t('co.metric.plan')} value={formatCurrency(totals?.planned || 0)} />
        <MetricCard label={t('co.metric.actual')} value={formatCurrency(totals?.actual || 0)} />
        <MetricCard label={t('co.metric.variance')} value={formatCurrency(totals?.variance || 0)}
          trendVariant={Number(totals?.variance || 0) >= 0 ? 'success' : 'danger'} />
      </div>

      <Table columns={columns} rows={rows} isLoading={report.isLoading} emptyMessage={t('co.empty')} />

      <SetPlanModal open={planOpen} onClose={() => setPlanOpen(false)} fiscalYear={year} />
      <PostCostModal open={postOpen} onClose={() => setPostOpen(false)} />
    </section>
  );
}
